import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import { useNavigate, Link } from 'react-router-dom';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import {
  MessageSquare, Search, Filter, Send, User, Phone, Mail,
  Clock, CheckCircle, AlertCircle, ChevronRight, X,
  Tag, UserPlus, Flag, MoreVertical, ExternalLink,
  Calendar, FileText, ArrowLeft, Inbox, CheckCheck, Smartphone,
  Bell, BellOff, Volume2, Archive, Undo2
} from 'lucide-react';
import { sendSMS } from '../utils/notificationsApi';
import './Messages.css';

// Type badge colors
const TYPE_COLORS = {
  general: { bg: '#e5e7eb', text: '#374151' },
  reschedule: { bg: '#dbeafe', text: '#1d4ed8' },
  addon: { bg: '#d1fae5', text: '#047857' },
  laundry: { bg: '#e0e7ff', text: '#4338ca' },
  feedback: { bg: '#fef3c7', text: '#b45309' },
  complaint: { bg: '#fee2e2', text: '#b91c1c' },
  urgent: { bg: '#fee2e2', text: '#b91c1c' }
};

// Source/channel badge config
const SOURCE_BADGES = {
  sms: { icon: '📱', label: 'SMS', bg: '#dbeafe', text: '#1d4ed8' },
  portal: { icon: '🌐', label: 'Portal', bg: '#e0e7ff', text: '#4338ca' },
  admin_compose: { icon: '✉️', label: 'Outbound', bg: '#d1fae5', text: '#047857' },
  email: { icon: '📧', label: 'Email', bg: '#fef3c7', text: '#b45309' }
};

// Priority badge colors
const PRIORITY_COLORS = {
  low: { bg: '#f3f4f6', text: '#6b7280' },
  normal: { bg: '#dbeafe', text: '#1d4ed8' },
  high: { bg: '#ffedd5', text: '#c2410c' },
  urgent: { bg: '#fee2e2', text: '#b91c1c' }
};

// Status badge colors
const STATUS_COLORS = {
  new: { bg: '#fee2e2', text: '#b91c1c' },
  read: { bg: '#fef3c7', text: '#b45309' },
  in_progress: { bg: '#dbeafe', text: '#1d4ed8' },
  resolved: { bg: '#d1fae5', text: '#047857' }
};

function Messages() {
  const navigate = useNavigate();
  const { 
    state, 
    getAdminUnreadCount, 
    getManagers,
    addMessage,
    addReply,
    updateMessage,
    markMessageReadByAdmin,
    assignMessage,
    resolveMessage,
    logClientCommunication
  } = useApp();
  
  // UI State
  const [selectedMessageId, setSelectedMessageId] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterAssigned, setFilterAssigned] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  
  // Compose new message state
  const [showCompose, setShowCompose] = useState(false);
  const [composeClientId, setComposeClientId] = useState('');
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [composeSending, setComposeSending] = useState(false);
  const [composeSearch, setComposeSearch] = useState('');
  const [composeChannel, setComposeChannel] = useState('email'); // 'email' | 'sms' | 'both'
  
  // Reply channel state
  const [replyChannel, setReplyChannel] = useState('email'); // 'email' | 'sms' | 'both'

  // Browser notification state
  const [notificationsEnabled, setNotificationsEnabled] = useState(
    typeof Notification !== 'undefined' && Notification.permission === 'granted'
  );
  const prevMessageCountRef = useRef(0);
  const notificationSoundRef = useRef(null);

  // Get managers for assignment dropdown
  const managers = useMemo(() => getManagers(), [state.employees]);

  // Filter and sort messages
  const filteredMessages = useMemo(() => {
    let messages = [...state.messages];
    
    // Archive filter - hide archived by default unless viewing archived
    if (showArchived) {
      messages = messages.filter(m => m.archived === true);
    } else {
      messages = messages.filter(m => !m.archived);
    }
    
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      messages = messages.filter(m => 
        m.clientName?.toLowerCase().includes(query) ||
        m.subject?.toLowerCase().includes(query) ||
        m.thread?.some(t => t.body?.toLowerCase().includes(query))
      );
    }
    
    // Status filter
    if (filterStatus !== 'all') {
      messages = messages.filter(m => m.status === filterStatus);
    }
    
    // Type filter
    if (filterType !== 'all') {
      messages = messages.filter(m => m.type === filterType);
    }
    
    // Priority filter
    if (filterPriority !== 'all') {
      messages = messages.filter(m => m.priority === filterPriority);
    }
    
    // Assignment filter
    if (filterAssigned === 'unassigned') {
      messages = messages.filter(m => !m.assignedTo);
    } else if (filterAssigned !== 'all') {
      messages = messages.filter(m => m.assignedTo === filterAssigned);
    }
    
    // Sort by last message time (newest first)
    messages.sort((a, b) => 
      new Date(b.lastMessageAt || b.createdAt) - new Date(a.lastMessageAt || a.createdAt)
    );
    
    return messages;
  }, [state.messages, searchQuery, filterStatus, filterType, filterPriority, filterAssigned, showArchived]);

  // Get selected message
  const selectedMessage = useMemo(() => {
    if (!selectedMessageId) return null;
    return state.messages.find(m => m.id === selectedMessageId);
  }, [selectedMessageId, state.messages]);

  // Mark as read when selecting a message
  useEffect(() => {
    if (selectedMessage && (selectedMessage.status === 'new' || !selectedMessage.lastAdminReadAt)) {
      markMessageReadByAdmin(selectedMessage.id);
    }
    // Auto-set reply channel based on message source
    if (selectedMessage?.source === 'sms') {
      setReplyChannel('sms');
    } else {
      setReplyChannel('email');
    }
  }, [selectedMessageId]);

  // Stats
  const stats = useMemo(() => {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    return {
      new: state.messages.filter(m => m.status === 'new').length,
      inProgress: state.messages.filter(m => m.status === 'in_progress').length,
      resolvedToday: state.messages.filter(m => 
        m.status === 'resolved' && 
        m.resolvedAt && 
        new Date(m.resolvedAt) >= todayStart
      ).length
    };
  }, [state.messages]);

  // Check if message has unread client reply
  const hasUnreadClientReply = (message) => {
    if (!message.lastClientMessageAt) return false;
    if (!message.lastAdminReadAt) return true;
    return new Date(message.lastClientMessageAt) > new Date(message.lastAdminReadAt);
  };

  // ===== BROWSER NOTIFICATIONS =====
  
  const enableNotifications = useCallback(async () => {
    if (typeof Notification === 'undefined') return;
    const perm = await Notification.requestPermission();
    setNotificationsEnabled(perm === 'granted');
  }, []);

  // Detect new inbound messages and fire browser notification + sound
  useEffect(() => {
    const newMessages = state.messages.filter(m => m.status === 'new');
    const newCount = newMessages.length;

    // Only notify if count went UP (new message arrived), not on initial load
    if (prevMessageCountRef.current > 0 && newCount > prevMessageCountRef.current) {
      // Find the newest new message
      const latest = newMessages.sort((a, b) =>
        new Date(b.lastMessageAt || b.createdAt) - new Date(a.lastMessageAt || a.createdAt)
      )[0];

      if (latest) {
        const senderName = latest.clientName || 'Unknown';
        const preview = latest.thread?.[latest.thread.length - 1]?.body?.substring(0, 80) || '';
        const isSms = latest.source === 'sms';

        // Play notification sound
        try {
          if (!notificationSoundRef.current) {
            // Use a short beep via AudioContext
            const ctx = new (window.AudioContext || window.webkitAudioContext)();
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.frequency.value = 800;
            gain.gain.value = 0.15;
            osc.start();
            osc.stop(ctx.currentTime + 0.15);
            // Second beep
            setTimeout(() => {
              try {
                const ctx2 = new (window.AudioContext || window.webkitAudioContext)();
                const osc2 = ctx2.createOscillator();
                const gain2 = ctx2.createGain();
                osc2.connect(gain2);
                gain2.connect(ctx2.destination);
                osc2.frequency.value = 1000;
                gain2.gain.value = 0.15;
                osc2.start();
                osc2.stop(ctx2.currentTime + 0.15);
              } catch (e) { /* silent */ }
            }, 180);
          }
        } catch (e) { /* AudioContext not available */ }

        // Browser notification
        if (notificationsEnabled && typeof Notification !== 'undefined') {
          try {
            const notif = new Notification(
              `${isSms ? '📱 Text' : '💬 Message'} from ${senderName}`, {
                body: preview || 'New message received',
                icon: '/favicon.ico',
                tag: `msg-${latest.id}`,
                requireInteraction: true
              }
            );
            notif.onclick = () => {
              window.focus();
              setSelectedMessageId(latest.id);
              notif.close();
            };
          } catch (e) { /* notification failed */ }
        }
      }
    }

    prevMessageCountRef.current = newCount;
  }, [state.messages, notificationsEnabled]);

  // Handle sending reply
  const handleSendReply = async () => {
    if (!replyText.trim() || !selectedMessage) return;
    
    setSending(true);
    try {
      await addReply(selectedMessage.id, {
        body: replyText,
        sender: 'admin',
        senderName: 'Sarah',
        senderEmail: 'sarah@twosisterscleaningco.com',
        channel: replyChannel
      });
      
      let emailSent = false;
      let smsSent = false;
      
      // Send via EMAIL if channel is 'email' or 'both'
      if (replyChannel === 'email' || replyChannel === 'both') {
        if (selectedMessage.clientEmail) {
          try {
            const emailResponse = await fetch('/.netlify/functions/send-email', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                type: 'admin_message_reply',
                data: {
                  clientEmail: selectedMessage.clientEmail,
                  clientName: selectedMessage.clientName?.split(' ')[0] || 'there',
                  subject: selectedMessage.subject,
                  message: replyText.trim()
                }
              })
            });
            emailSent = emailResponse.ok;
          } catch (emailError) {
            console.error('Failed to send notification email:', emailError);
          }
        }
      }
      
      // Send via SMS if channel is 'sms' or 'both'
      if (replyChannel === 'sms' || replyChannel === 'both') {
        const clientPhone = selectedMessage.clientPhone;
        if (clientPhone) {
          try {
            const smsResult = await sendSMS('direct_sms', {
              phone: clientPhone,
              message: replyText.trim()
            });
            smsSent = smsResult.success;
            if (!smsResult.success) {
              console.error('SMS send failed:', smsResult.error);
            }
          } catch (smsError) {
            console.error('Failed to send SMS reply:', smsError);
          }
        }
      }
      
      // Log the communication
      if (selectedMessage.clientId) {
        const channels = [];
        if (emailSent) channels.push('email');
        if (smsSent) channels.push('sms');
        
        await logClientCommunication(selectedMessage.clientId, {
          type: replyChannel === 'sms' ? 'sms' : 'email',
          category: 'portal_message',
          subject: `Reply: ${selectedMessage.subject}`,
          recipient: replyChannel === 'sms' ? selectedMessage.clientPhone : selectedMessage.clientEmail,
          status: (emailSent || smsSent) ? 'sent' : 'failed',
          details: {
            messageId: selectedMessage.id,
            replyPreview: replyText.substring(0, 100) + (replyText.length > 100 ? '...' : ''),
            channels: channels
          }
        });
      }
      
      setReplyText('');
    } catch (error) {
      console.error('Failed to send reply:', error);
      alert('Failed to send reply. Please try again.');
    }
    setSending(false);
  };

  // Handle status change
  const handleStatusChange = async (status) => {
    if (!selectedMessage) return;
    await updateMessage(selectedMessage.id, { status });
  };

  // Handle priority change
  const handlePriorityChange = async (priority) => {
    if (!selectedMessage) return;
    await updateMessage(selectedMessage.id, { priority });
  };

  // Handle type change
  const handleTypeChange = async (type) => {
    if (!selectedMessage) return;
    await updateMessage(selectedMessage.id, { type });
  };

  // Handle assignment change
  const handleAssignmentChange = async (employeeId) => {
    if (!selectedMessage) return;
    await assignMessage(selectedMessage.id, employeeId || null);
  };

  // Handle resolve
  const handleResolve = async () => {
    if (!selectedMessage) return;
    await resolveMessage(selectedMessage.id);
  };

  const handleArchive = async () => {
    if (!selectedMessage) return;
    await updateMessage(selectedMessage.id, { archived: true, archivedAt: new Date().toISOString() });
    setSelectedMessageId(null);
  };

  const handleUnarchive = async () => {
    if (!selectedMessage) return;
    await updateMessage(selectedMessage.id, { archived: false, archivedAt: null });
  };

  // === COMPOSE NEW MESSAGE ===
  
  // All clients for compose dropdown (not just active - you might need to text anyone)
  const activeClients = useMemo(() => {
    return state.clients
      .filter(c => c.status !== 'archived') // Exclude archived, include everything else
      .sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`));
  }, [state.clients]);

  // Filter clients by search in compose
  const filteredClients = useMemo(() => {
    if (!composeSearch) return activeClients;
    const q = composeSearch.toLowerCase();
    return activeClients.filter(c =>
      `${c.firstName} ${c.lastName}`.toLowerCase().includes(q) ||
      (c.email || '').toLowerCase().includes(q) ||
      (c.phone || '').includes(q)
    );
  }, [activeClients, composeSearch]);

  const handleSendNewMessage = async () => {
    if (!composeClientId || !composeBody.trim()) return;
    // SMS compose doesn't require a subject
    if (composeChannel === 'email' && !composeSubject.trim()) return;
    // Guard against double-click
    if (composeSending) return;
    setComposeSending(true);
    try {
      const client = state.clients.find(c => c.id === composeClientId);
      const isSms = composeChannel === 'sms';
      const isBoth = composeChannel === 'both';

      let targetMessageId = null;

      // For SMS: check if there's already an open SMS thread with this client
      if (isSms || isBoth) {
        const existingThread = state.messages.find(m =>
          m.clientId === composeClientId &&
          m.source === 'sms' &&
          m.status !== 'resolved'
        );
        if (existingThread) {
          // Append to existing thread instead of creating a new one
          await addReply(existingThread.id, {
            body: composeBody.trim(),
            sender: 'admin',
            senderName: 'Sarah',
            senderEmail: 'sarah@twosisterscleaningco.com',
            channel: 'sms'
          });
          targetMessageId = existingThread.id;
        }
      }

      // If we didn't append to an existing thread, create a new message
      if (!targetMessageId) {
        const newMsg = await addMessage({
          clientId: composeClientId,
          subject: isSms ? `SMS with ${client?.firstName || 'Client'}` : composeSubject.trim(),
          body: composeBody.trim(),
          sender: 'admin',
          senderName: 'Sarah',
          senderEmail: 'sarah@twosisterscleaningco.com',
          source: isSms ? 'sms' : 'admin_compose',
          type: 'general',
          priority: 'normal'
        });
        targetMessageId = newMsg?.id;
      }

      let emailSent = false;
      let smsSent = false;

      // Send email if channel is 'email' or 'both'
      if ((composeChannel === 'email' || isBoth) && client?.email) {
        try {
          await fetch('/.netlify/functions/send-email', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              type: 'admin_message_reply',
              data: {
                clientEmail: client.email,
                clientName: client.firstName || 'there',
                subject: composeSubject.trim() || 'Message from Two Sisters Cleaning',
                message: composeBody.trim()
              }
            })
          });
          emailSent = true;
        } catch (emailError) {
          console.error('Failed to send notification email:', emailError);
        }
      }

      // Send SMS if channel is 'sms' or 'both' — use direct_sms for clean text
      if ((isSms || isBoth) && client?.phone) {
        try {
          const smsResult = await sendSMS('direct_sms', {
            phone: client.phone,
            message: composeBody.trim()
          });
          smsSent = smsResult.success;
          if (!smsResult.success) {
            console.error('SMS compose send failed:', smsResult.error);
            if (isSms) alert(`SMS failed: ${smsResult.error || 'Unknown error'}`);
          }
        } catch (smsError) {
          console.error('Failed to send SMS:', smsError);
          if (isSms) alert('Failed to send SMS. Check Twilio configuration.');
        }
      }

      // Log communication
      if (client) {
        const channels = [];
        if (emailSent) channels.push('email');
        if (smsSent) channels.push('sms');
        
        await logClientCommunication(composeClientId, {
          type: isSms ? 'sms' : 'email',
          category: 'portal_message',
          subject: composeSubject.trim() || `SMS to ${client.firstName}`,
          recipient: isSms ? client.phone : client.email,
          status: (emailSent || smsSent) ? 'sent' : 'failed',
          details: { messageId: targetMessageId, isOutbound: true, channels }
        });
      }

      // Reset compose and select the message
      setShowCompose(false);
      setComposeClientId('');
      setComposeSubject('');
      setComposeBody('');
      setComposeSearch('');
      setComposeChannel('email');
      if (targetMessageId) setSelectedMessageId(targetMessageId);
    } catch (error) {
      console.error('Failed to send message:', error);
      alert('Failed to send message. Please try again.');
    }
    setComposeSending(false);
  };

  // Format time for display
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = parseISO(timestamp);
    const now = new Date();
    const diffHours = (now - date) / (1000 * 60 * 60);
    
    if (diffHours < 24) {
      return formatDistanceToNow(date, { addSuffix: true });
    } else if (diffHours < 168) { // 7 days
      return format(date, 'EEE h:mm a');
    } else {
      return format(date, 'MMM d');
    }
  };

  // Get last message preview
  const getLastMessagePreview = (message) => {
    if (!message.thread || message.thread.length === 0) return '';
    const lastMessage = message.thread[message.thread.length - 1];
    const preview = lastMessage.body || '';
    return preview.length > 100 ? preview.substring(0, 100) + '...' : preview;
  };

  return (
    <div className="messages-page">
      {/* Header */}
      <div className="messages-header">
        <div className="messages-title">
          <MessageSquare size={24} />
          <h1>Client Messages</h1>
        </div>
        
        <button className="compose-btn" onClick={() => setShowCompose(true)}>
          <Send size={16} />
          New Message
        </button>

        <button 
          className={`notification-toggle-btn ${notificationsEnabled ? 'enabled' : ''}`}
          onClick={enableNotifications}
          title={notificationsEnabled ? 'Notifications enabled' : 'Enable browser notifications'}
        >
          {notificationsEnabled ? <Bell size={16} /> : <BellOff size={16} />}
        </button>
        
        <div className="messages-stats">
          <div className="stat-badge new">
            <span className="stat-count">{stats.new}</span>
            <span className="stat-label">New</span>
          </div>
          <div className="stat-badge in-progress">
            <span className="stat-count">{stats.inProgress}</span>
            <span className="stat-label">In Progress</span>
          </div>
          <div className="stat-badge resolved">
            <span className="stat-count">{stats.resolvedToday}</span>
            <span className="stat-label">Resolved Today</span>
          </div>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="messages-filters">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search messages..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        
        <div className="filter-dropdowns">
          <select 
            value={filterStatus} 
            onChange={(e) => setFilterStatus(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Status</option>
            <option value="new">New</option>
            <option value="read">Read</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
          </select>
          
          <select 
            value={filterType} 
            onChange={(e) => setFilterType(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Types</option>
            <option value="general">General</option>
            <option value="reschedule">Reschedule</option>
            <option value="addon">Add-on</option>
            <option value="laundry">Laundry</option>
            <option value="feedback">Feedback</option>
            <option value="complaint">Complaint</option>
          </select>
          
          <select 
            value={filterPriority} 
            onChange={(e) => setFilterPriority(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Priority</option>
            <option value="low">Low</option>
            <option value="normal">Normal</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
          
          <select 
            value={filterAssigned} 
            onChange={(e) => setFilterAssigned(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Assigned</option>
            <option value="unassigned">Unassigned</option>
            {managers.map(m => (
              <option key={m.id} value={m.id}>
                {m.firstName} {m.lastName}
              </option>
            ))}
          </select>

          <button 
            className={`archive-toggle-btn ${showArchived ? 'active' : ''}`}
            onClick={() => { setShowArchived(!showArchived); setSelectedMessageId(null); }}
            title={showArchived ? 'Show active messages' : 'Show archived messages'}
          >
            <Archive size={16} />
            {showArchived ? 'Viewing Archived' : 'Archived'}
            {!showArchived && state.messages.filter(m => m.archived).length > 0 && (
              <span className="archive-count">{state.messages.filter(m => m.archived).length}</span>
            )}
          </button>
        </div>
      </div>

      {/* Main Content - Split View */}
      <div className="messages-content">
        {/* Message List */}
        <div className="message-list">
          {filteredMessages.length === 0 ? (
            <div className="empty-state">
              <Inbox size={48} />
              <h3>No messages</h3>
              <p>
                {searchQuery || filterStatus !== 'all' || filterType !== 'all' 
                  ? 'No messages match your filters' 
                  : 'Messages from clients will appear here'}
              </p>
            </div>
          ) : (
            filteredMessages.map(message => (
              <div
                key={message.id}
                className={`message-card ${selectedMessageId === message.id ? 'selected' : ''} ${hasUnreadClientReply(message) || message.status === 'new' ? 'unread' : ''}`}
                onClick={() => setSelectedMessageId(message.id)}
              >
                <div className="message-card-header">
                  <span className="client-name">{message.clientName}</span>
                  <span className="message-time">{formatTime(message.lastMessageAt)}</span>
                </div>
                
                <div className="message-subject">{message.subject}</div>
                
                <div className="message-preview">{getLastMessagePreview(message)}</div>
                
                <div className="message-card-footer">
                  <div className="message-badges">
                    {/* Source/channel badge */}
                    {SOURCE_BADGES[message.source] && (
                      <span 
                        className="badge source-badge"
                        style={{ 
                          backgroundColor: SOURCE_BADGES[message.source].bg,
                          color: SOURCE_BADGES[message.source].text
                        }}
                      >
                        {SOURCE_BADGES[message.source].icon} {SOURCE_BADGES[message.source].label}
                      </span>
                    )}
                    
                    <span 
                      className="badge type-badge"
                      style={{ 
                        backgroundColor: TYPE_COLORS[message.type]?.bg,
                        color: TYPE_COLORS[message.type]?.text
                      }}
                    >
                      {message.type}
                    </span>
                    
                    {(message.priority === 'high' || message.priority === 'urgent') && (
                      <span 
                        className="badge priority-badge"
                        style={{ 
                          backgroundColor: PRIORITY_COLORS[message.priority]?.bg,
                          color: PRIORITY_COLORS[message.priority]?.text
                        }}
                      >
                        {message.priority}
                      </span>
                    )}
                    
                    <span 
                      className="badge status-badge"
                      style={{ 
                        backgroundColor: STATUS_COLORS[message.status]?.bg,
                        color: STATUS_COLORS[message.status]?.text
                      }}
                    >
                      {message.status?.replace('_', ' ')}
                    </span>
                  </div>
                  
                  {message.assignedToName && (
                    <span className="assigned-to">
                      <User size={12} />
                      {message.assignedToName}
                    </span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Conversation Panel */}
        <div className={`conversation-panel ${selectedMessage ? 'active' : ''}`}>
          {selectedMessage ? (
            <>
              {/* Conversation Header */}
              <div className="conversation-header">
                <button 
                  className="back-btn mobile-only"
                  onClick={() => setSelectedMessageId(null)}
                >
                  <ArrowLeft size={20} />
                </button>
                
                <div className="conversation-client-info">
                  <h2>{selectedMessage.clientName}</h2>
                  <div className="client-contact">
                    {selectedMessage.clientEmail && (
                      <a href={`mailto:${selectedMessage.clientEmail}`}>
                        <Mail size={14} />
                        {selectedMessage.clientEmail}
                      </a>
                    )}
                    {selectedMessage.clientPhone && (
                      <a href={`tel:${selectedMessage.clientPhone}`}>
                        <Phone size={14} />
                        {selectedMessage.clientPhone}
                      </a>
                    )}
                  </div>
                </div>
                
                <Link 
                  to={`/clients/${selectedMessage.clientId}`}
                  className="view-client-btn"
                >
                  <ExternalLink size={16} />
                  View Client
                </Link>
              </div>

              {/* Message Actions Bar */}
              <div className="message-actions-bar">
                <div className="action-group">
                  <label>Status</label>
                  <select 
                    value={selectedMessage.status}
                    onChange={(e) => handleStatusChange(e.target.value)}
                  >
                    <option value="new">New</option>
                    <option value="read">Read</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                  </select>
                </div>
                
                <div className="action-group">
                  <label>Priority</label>
                  <select 
                    value={selectedMessage.priority}
                    onChange={(e) => handlePriorityChange(e.target.value)}
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
                
                <div className="action-group">
                  <label>Type</label>
                  <select 
                    value={selectedMessage.type}
                    onChange={(e) => handleTypeChange(e.target.value)}
                  >
                    <option value="general">General</option>
                    <option value="reschedule">Reschedule</option>
                    <option value="addon">Add-on</option>
                    <option value="laundry">Laundry</option>
                    <option value="feedback">Feedback</option>
                    <option value="complaint">Complaint</option>
                  </select>
                </div>
                
                <div className="action-group">
                  <label>Assigned To</label>
                  <select 
                    value={selectedMessage.assignedTo || ''}
                    onChange={(e) => handleAssignmentChange(e.target.value)}
                  >
                    <option value="">Unassigned</option>
                    {managers.map(m => (
                      <option key={m.id} value={m.id}>
                        {m.firstName} {m.lastName}
                      </option>
                    ))}
                  </select>
                </div>
                
                {selectedMessage.status !== 'resolved' && (
                  <button 
                    className="resolve-btn"
                    onClick={handleResolve}
                  >
                    <CheckCheck size={16} />
                    Mark Resolved
                  </button>
                )}

                {selectedMessage.archived ? (
                  <button 
                    className="archive-btn unarchive"
                    onClick={handleUnarchive}
                  >
                    <Undo2 size={16} />
                    Unarchive
                  </button>
                ) : (
                  <button 
                    className="archive-btn"
                    onClick={handleArchive}
                  >
                    <Archive size={16} />
                    Archive
                  </button>
                )}
              </div>

              {/* Subject */}
              <div className="conversation-subject">
                <strong>Subject:</strong> {selectedMessage.subject}
              </div>

              {/* Thread */}
              <div className="conversation-thread">
                {selectedMessage.thread?.map((msg, idx) => (
                  <div 
                    key={msg.id || idx}
                    className={`thread-message ${msg.sender === 'admin' ? 'admin' : 'client'}`}
                  >
                    <div className="thread-message-header">
                      <span className="sender-name">
                        {msg.senderName}
                        {msg.channel === 'sms' && <span className="channel-tag sms">SMS</span>}
                        {msg.channel === 'email' && <span className="channel-tag email">Email</span>}
                      </span>
                      <span className="message-timestamp">
                        {format(parseISO(msg.timestamp), 'MMM d, h:mm a')}
                      </span>
                    </div>
                    <div className="thread-message-body">
                      {msg.body}
                    </div>
                    {msg.mediaUrls && msg.mediaUrls.length > 0 && (
                      <div className="thread-message-media">
                        {msg.mediaUrls.map((media, mIdx) => (
                          <a key={mIdx} href={media.url} target="_blank" rel="noopener noreferrer" className="media-link">
                            📎 {media.contentType?.startsWith('image') ? 'Photo' : 'Attachment'}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Reply Box */}
              <div className="reply-box">
                <div className="reply-channel-selector">
                  <span className="reply-channel-label">Reply via:</span>
                  <button 
                    className={`channel-btn ${replyChannel === 'email' ? 'active' : ''}`}
                    onClick={() => setReplyChannel('email')}
                    disabled={!selectedMessage?.clientEmail}
                    title={selectedMessage?.clientEmail || 'No email on file'}
                  >
                    <Mail size={14} /> Email
                  </button>
                  <button 
                    className={`channel-btn sms ${replyChannel === 'sms' ? 'active' : ''}`}
                    onClick={() => setReplyChannel('sms')}
                    disabled={!selectedMessage?.clientPhone}
                    title={selectedMessage?.clientPhone || 'No phone on file'}
                  >
                    <Smartphone size={14} /> SMS
                  </button>
                  <button 
                    className={`channel-btn both ${replyChannel === 'both' ? 'active' : ''}`}
                    onClick={() => setReplyChannel('both')}
                    disabled={!selectedMessage?.clientEmail || !selectedMessage?.clientPhone}
                  >
                    Both
                  </button>
                </div>
                <textarea
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder={replyChannel === 'sms' ? 'Type your text message...' : 'Type your reply...'}
                  rows={3}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                      handleSendReply();
                    }
                  }}
                />
                {replyChannel === 'sms' && (
                  <div className="sms-char-count">
                    {replyText.length}/160 characters {replyText.length > 160 ? `(${Math.ceil(replyText.length / 160)} segments)` : ''}
                  </div>
                )}
                <div className="reply-actions">
                  <span className="keyboard-hint">⌘+Enter to send</span>
                  <button 
                    className={`send-btn ${replyChannel === 'sms' ? 'sms-send' : ''}`}
                    onClick={handleSendReply}
                    disabled={!replyText.trim() || sending}
                  >
                    {replyChannel === 'sms' ? <Smartphone size={16} /> : <Send size={16} />}
                    {sending ? 'Sending...' : replyChannel === 'sms' ? 'Send Text' : replyChannel === 'both' ? 'Send Both' : 'Send Reply'}
                  </button>
                </div>
              </div>
            </>
          ) : (
            <div className="no-selection">
              <MessageSquare size={48} />
              <h3>Select a message</h3>
              <p>Choose a conversation from the list to view details and reply</p>
            </div>
          )}
        </div>
      </div>

      {/* Compose New Message Modal */}
      {showCompose && (
        <div className="compose-overlay" onClick={() => setShowCompose(false)}>
          <div className="compose-modal" onClick={e => e.stopPropagation()}>
            <div className="compose-header">
              <h2>{composeChannel === 'sms' ? 'New Text Message' : 'New Message to Client'}</h2>
              <button className="compose-close" onClick={() => setShowCompose(false)}><X size={20} /></button>
            </div>

            <div className="compose-body">
              {/* Channel selector */}
              <div className="compose-field">
                <label>Send Via</label>
                <div className="compose-channel-selector">
                  <button 
                    className={`channel-btn ${composeChannel === 'email' ? 'active' : ''}`}
                    onClick={() => setComposeChannel('email')}
                  >
                    <Mail size={14} /> Email
                  </button>
                  <button 
                    className={`channel-btn sms ${composeChannel === 'sms' ? 'active' : ''}`}
                    onClick={() => setComposeChannel('sms')}
                  >
                    <Smartphone size={14} /> Text
                  </button>
                  <button 
                    className={`channel-btn both ${composeChannel === 'both' ? 'active' : ''}`}
                    onClick={() => setComposeChannel('both')}
                  >
                    Both
                  </button>
                </div>
              </div>

              {/* Client picker */}
              <div className="compose-field">
                <label>To (Client)</label>
                {composeClientId ? (
                  <div className="compose-selected-client">
                    <span>
                      {(() => { 
                        const c = state.clients.find(cl => cl.id === composeClientId); 
                        if (!c) return 'Unknown';
                        const name = `${c.firstName} ${c.lastName}`;
                        const contact = composeChannel === 'sms' ? (c.phone || 'No phone') : (c.email || 'No email');
                        return `${name} — ${contact}`;
                      })()}
                    </span>
                    <button onClick={() => { setComposeClientId(''); setComposeSearch(''); }}><X size={14} /></button>
                  </div>
                ) : (
                  <>
                    <div className="compose-client-search">
                      <Search size={16} />
                      <input
                        autoFocus
                        value={composeSearch}
                        onChange={e => setComposeSearch(e.target.value)}
                        placeholder="Search clients by name, email, or phone..."
                      />
                    </div>
                    {composeSearch && (
                      <div className="compose-client-list">
                        {filteredClients.length === 0 && <div className="compose-client-empty">No matching clients</div>}
                        {filteredClients.slice(0, 8).map(c => {
                          const hasChannel = composeChannel === 'sms' ? !!c.phone : composeChannel === 'email' ? !!c.email : (!!c.email || !!c.phone);
                          return (
                            <button 
                              key={c.id} 
                              className={`compose-client-option ${!hasChannel ? 'disabled' : ''}`} 
                              onClick={() => { if (hasChannel) { setComposeClientId(c.id); setComposeSearch(''); } }}
                              disabled={!hasChannel}
                            >
                              <div className="compose-client-avatar">{c.firstName?.[0]}{c.lastName?.[0]}</div>
                              <div className="compose-client-info">
                                <span className="compose-client-name">{c.firstName} {c.lastName}</span>
                                <span className="compose-client-email">
                                  {composeChannel === 'sms' ? (c.phone || '❌ No phone') : (c.email || c.phone || '')}
                                </span>
                              </div>
                              {!hasChannel && <span style={{ fontSize: '0.7rem', color: '#ef4444' }}>No {composeChannel === 'sms' ? 'phone' : 'email'}</span>}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Subject — hide for SMS-only */}
              {composeChannel !== 'sms' && (
                <div className="compose-field">
                  <label>Subject</label>
                  <input
                    value={composeSubject}
                    onChange={e => setComposeSubject(e.target.value)}
                    placeholder="Message subject..."
                  />
                </div>
              )}

              {/* Body */}
              <div className="compose-field">
                <label>{composeChannel === 'sms' ? 'Text Message' : 'Message'}</label>
                <textarea
                  value={composeBody}
                  onChange={e => setComposeBody(e.target.value)}
                  placeholder={composeChannel === 'sms' ? 'Type your text message...' : 'Type your message to the client...'}
                  rows={composeChannel === 'sms' ? 4 : 6}
                />
                {composeChannel === 'sms' && (
                  <div className="sms-char-count" style={{ marginTop: 4 }}>
                    {composeBody.length}/160 characters {composeBody.length > 160 ? `(${Math.ceil(composeBody.length / 160)} segments)` : ''}
                  </div>
                )}
              </div>
            </div>

            <div className="compose-footer">
              <p className="compose-note">
                {composeChannel === 'sms' 
                  ? 'Text will be sent from your Two Sisters toll-free number.' 
                  : composeChannel === 'both'
                  ? 'Email + text will be sent. Message also appears in client portal.'
                  : 'This will appear in the client\'s portal and they\'ll get an email notification.'}
              </p>
              <div className="compose-actions">
                <button className="compose-cancel" onClick={() => setShowCompose(false)}>Cancel</button>
                <button
                  className={`compose-send ${composeChannel === 'sms' ? 'sms-send' : ''}`}
                  disabled={composeSending || !composeClientId || !composeBody.trim() || (composeChannel !== 'sms' && !composeSubject.trim())}
                  onClick={handleSendNewMessage}
                >
                  {composeSending ? 'Sending...' : (
                    <>
                      {composeChannel === 'sms' ? <Smartphone size={14} /> : <Send size={14} />}
                      {composeChannel === 'sms' ? ' Send Text' : composeChannel === 'both' ? ' Send Both' : ' Send Message'}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Messages;
